import {
  GridColDef,
  GridActionsCellItem,
  GridActionsColDef,
} from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip, SpeedDial } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import mobileApplicationThemeService from '../../services/mobileApplicationThemeService';
import { formatTime } from '../../utils/formatters';

const MobileApplicationThemesPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();

  const { checkPermission } = useCheckPermission();

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    mobileApplicationThemeService
      .list()
      .then((response) => {
        if (!response.hasError) {
          setRows(response.records);
        } else
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const getActions = (params: GridRowParams) => {
    var actions: any[] = [];

    let readonly = !checkPermission(['UpdateMobileApplicationTheme']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="View">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'View' : 'Edit'}
        onClick={() =>
          navigate(`/mobileApplicationTheme/edit/${params.row.id}`)
        }
      />
    );

    if (checkPermission(['DeleteMobileApplicationTheme'])) {
      actions.push(
        <GridActionsCellItem
          color="primary"
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() =>
            setParams({
              open: true,
              name: 'Are you sure to delete this item?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    {
      field: 'startDate',
      headerName: 'Kezdés dátum',
      flex: 100,
      valueFormatter: (value: any) => formatTime(value, ''),
    },
    {
      field: 'endDate',
      headerName: 'Vég dátum',
      flex: 100,
      valueFormatter: (value: any) => formatTime(value, ''),
    },
    {
      field: 'primaryColor',
      headerName: 'Elsődleges',
      flex: 100,
      renderCell: (params: any) => (
        <div
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: params.value,
            borderRadius: '50%',
          }}
        ></div>
      ),
    },
    {
      field: 'secondaryColor',
      headerName: 'Másodlagos',
      flex: 100,
      renderCell: (params: any) => (
        <div
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: params.value,
            borderRadius: '50%',
          }}
        ></div>
      ),
    },
    {
      field: 'iconPrimaryColor',
      headerName: 'Elsődleges ikon',
      flex: 100,
      renderCell: (params: any) => (
        <div
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: params.value,
            borderRadius: '50%',
          }}
        ></div>
      ),
    },
    {
      field: 'iconSecondaryColor',
      headerName: 'Másodlagos ikon',
      flex: 100,
      renderCell: (params: any) => (
        <div
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: params.value,
            borderRadius: '50%',
          }}
        ></div>
      ),
    },
    {
      field: 'illustrationPrimaryColor',
      headerName: 'Elsődleges illusztráció',
      flex: 100,
      renderCell: (params: any) => (
        <div
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: params.value,
            borderRadius: '50%',
          }}
        ></div>
      ),
    },

    {
      field: 'actions',
      type: 'actions',
      flex: 100,
      align: 'right',
      getActions: getActions,
    } as any,
  ];

  const handleDelete = (id: number) => {
    mobileApplicationThemeService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés!', {
          variant: 'success',
        });
        setRows(rows.filter((row) => row.id !== id));
      }
    });
  };

  return (
    <Paper>
      <Grid container sx={{
        p: 3
      }}>
        <Grid size={12}>
          <h2> Applikáció témák</h2>
        </Grid>
        {checkPermission(['CreateMobileApplicationTheme']) && (
          <Grid
            container
            size={12}
            sx={{
              p: 2,
              justifyContent: "end"
            }}>
            <Grid sx={{ display: { xs: 'none', sm: 'fixed' } }}>
              <Tooltip title="Applikáció téma létrehozása">
                <IconButton
                  component={RouterLink}
                  to={`/mobileApplicationTheme/create`}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDeleteDialog />
        <SpeedDial
          onClick={() => {
            navigate(`/mobileApplicationTheme/create`);
          }}
          sx={{
            display: { xs: 'fixed', sm: 'none' },
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          icon={<AddIcon />}
          ariaLabel={'Hozzáadás'}
        ></SpeedDial>
      </Grid>
    </Paper>
  );
};

export default MobileApplicationThemesPage;
